import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Snackbar, Alert, Button, CircularProgress } from "@mui/material";
import type { AlertColor } from "@mui/material/Alert";
import { getNotificationIconColor } from "../utils/notificationUtils";

export interface NotificationOptions {
  /** Duration to show the notification (default: 4000ms for success, 6000ms for error) */
  duration?: number;
  /** Position of the notification */
  anchorOrigin?: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  /** Optional action button rendered on the notification */
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  /** Optional element to render the toast inside the given container (panel) */
  containerEl?: HTMLElement | null;
}

export interface NotificationState {
  open: boolean;
  message: string;
  severity: AlertColor;
  duration: number;
  anchorOrigin: {
    vertical: "top" | "bottom";
    horizontal: "left" | "center" | "right";
  };
  action?: {
    label: string;
    onClick: () => void;
    loading?: boolean;
  };
  containerEl?: HTMLElement | null;
}

export interface NotificationActions {
  showSuccess: (message: string, options?: NotificationOptions) => void;
  showError: (message: string, options?: NotificationOptions) => void;
  showWarning: (message: string, options?: NotificationOptions) => void;
  showInfo: (message: string, options?: NotificationOptions) => void;
  hide: () => void;
  updateActionLoading: (loading: boolean) => void;
}

const DEFAULT_OPTIONS: NotificationOptions = {
  anchorOrigin: { vertical: "bottom", horizontal: "center" },
};

/**
 * Custom hook for managing notifications/snackbars
 * Centralizes all notification state and rendering logic
 */
export function useNotification(
  defaultOptions?: NotificationOptions,
): NotificationActions & { NotificationComponent: React.ReactElement } {
  const [notification, setNotification] = useState<NotificationState>({
    open: false,
    message: "",
    severity: "info",
    duration: 4000,
    anchorOrigin: { vertical: "bottom", horizontal: "center" },
  });

  const mergedDefaultOptions = useMemo(() => ({ ...DEFAULT_OPTIONS, ...defaultOptions }), [defaultOptions]);

  const showNotification = useCallback(
    (message: string, severity: AlertColor, options?: NotificationOptions) => {
      const finalOptions = { ...mergedDefaultOptions, ...options };
      const duration =
        finalOptions.duration || (severity === "error" ? 6000 : 4000);

      setNotification({
        open: true,
        message,
        severity,
        duration,
        anchorOrigin: finalOptions.anchorOrigin!,
        action: finalOptions.action,
        containerEl: finalOptions.containerEl ?? undefined,
      });
    },
    [mergedDefaultOptions],
  );

  const showSuccess = useCallback(
    (message: string, options?: NotificationOptions) => {
      showNotification(message, "success", options);
    },
    [showNotification],
  );

  const showError = useCallback(
    (message: string, options?: NotificationOptions) => {
      showNotification(message, "error", options);
    },
    [showNotification],
  );

  const showWarning = useCallback(
    (message: string, options?: NotificationOptions) => {
      showNotification(message, "warning", options);
    },
    [showNotification],
  );

  const showInfo = useCallback(
    (message: string, options?: NotificationOptions) => {
      showNotification(message, "info", options);
    },
    [showNotification],
  );

  const hide = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  const updateActionLoading = useCallback((loading: boolean) => {
    setNotification((prev) => ({
      ...prev,
      action: prev.action ? { ...prev.action, loading } : undefined,
    }));
  }, []);

  // Auto-dismiss timer for fixed toast
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set up auto-dismiss for fixed toast when it's open
    if (
      notification.open &&
      notification.containerEl &&
      notification.duration
    ) {
      timeoutRef.current = setTimeout(() => {
        hide();
      }, notification.duration);
    }

    // Cleanup on unmount or when notification changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [
    notification.open,
    notification.containerEl,
    notification.duration,
    hide,
  ]);

  const fixedToast =
    notification.containerEl && notification.open
      ? ReactDOM.createPortal(
          (() => {
            const rect = notification.containerEl!.getBoundingClientRect();
            const left = rect.left + rect.width / 2;
            return (
              <Box
                sx={{
                  position: "fixed",
                  left,
                  bottom: 16,
                  transform: "translateX(-50%)",
                  zIndex: (theme) => theme.zIndex.snackbar,
                }}
              >
                <Alert
                  onClose={hide}
                  severity={notification.severity}
                  variant="filled"
                  sx={{
                    width: "100%",
                    backgroundColor: "#323232",
                    color: "white",
                    "& .MuiAlert-icon": {
                      color: getNotificationIconColor(notification.severity),
                    },
                  }}
                  action={
                    notification.action ? (
                      <Button
                        color="primary"
                        size="small"
                        onClick={notification.action.onClick}
                        disabled={notification.action.loading}
                        sx={{
                          backgroundColor: "transparent",
                          color: "#89C9FB",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 255, 0.1)",
                          },
                        }}
                      >
                        {notification.action.loading ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "#89C9FB" }}
                          />
                        ) : (
                          notification.action.label
                        )}
                      </Button>
                    ) : undefined
                  }
                >
                  {notification.message}
                </Alert>
              </Box>
            );
          })(),
          document.body,
        )
      : null;

  const NotificationComponent = fixedToast ?? (
    <Snackbar
      open={notification.open}
      autoHideDuration={notification.duration}
      onClose={hide}
      anchorOrigin={notification.anchorOrigin}
    >
      <Alert
        onClose={hide}
        severity={notification.severity}
        variant="filled"
        sx={{
          width: "100%",
          backgroundColor: "#323232",
          color: "white",
          "& .MuiAlert-icon": {
            color: getNotificationIconColor(notification.severity),
          },
        }}
        action={
          notification.action ? (
            <Button
              color="inherit"
              size="small"
              onClick={notification.action.onClick}
              disabled={notification.action.loading}
              sx={{
                backgroundColor: "transparent",
                color: "#89C9FB",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 255, 0.1)",
                },
              }}
            >
              {notification.action.loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                notification.action.label
              )}
            </Button>
          ) : undefined
        }
      >
        {notification.message}
      </Alert>
    </Snackbar>
  );

  return {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hide,
    updateActionLoading,
    NotificationComponent,
  };
}
